// Local surface estimation for "Overfladedetektion" (navigation/SurfaceDetection.js):
// a least-squares/PCA plane through a small set of nearby point-cloud points,
// plus the fit-quality measures that fall out of the same eigen decomposition
// for free. Pure (no DOM/THREE) and allocation-light - it runs on an
// interactive hot path - so it works on a flat typed array and fills a
// caller-owned, reusable result object. Level 1 testable - see
// surfaceEstimator.test.js.
//
// ── Why a separate estimator and not localPlaneFit.js ─────────────────────
// The mathematical core IS shared: both use volume/referencePlane.js's
// jacobiEigenSymmetric3 (the project's one 3x3 symmetric eigen-solver), so
// there is one eigen kernel, not two. What differs is everything around it:
// localPlaneFit.fitPlaneFromPoints() takes an array of {x,y,z} objects
// (thousands of short-lived objects per call), runs a trim-and-refit loop,
// returns a deterministic *fallback* plane when support is insufficient
// ("never no plane" - right for Cirkel/Rektangel, which must always produce a
// figure) and discards the eigenvalues. Surface detection wants the opposite
// on each point: a flat typed array, no trim loop (an extra pass per
// mousemove for a display aid), eigenvalues kept for the quality measures,
// and an explicit "uncertain" result instead of a forced plane. Folding both
// into one function would have meant boolean mode flags on a hot path and a
// risk of changing Cirkel/Rektangel behaviour; sharing the eigen kernel gets
// the "no second PCA that drifts" benefit without that.
//
// ── Quality measures (all derived from the eigenvalues l0 <= l1 <= l2 of the
// covariance matrix; variances in m^2) ─────────────────────────────────────
//   rms        = sqrt(l0)          - spread of the points off the fitted plane
//   planarity  = 1 - l0 / l1       - 1: perfectly flat; ~0: no preferred plane
//   linearity  = 1 - l1 / l2       - ~0: collinear points (plane undefined)
//   curvature  = l0 / (l0+l1+l2)   - surface variation (0 flat .. 1/3 isotropic)
// They are kept as separate fields rather than folded into one "quality"
// number: future features (slope, aspect, flatness) can read the same fit.
//
// (The acceptance gate that used to live here belonged to the removed neighbourhood pipeline; the front-layer
// pick has its own planarity threshold, see geometry/frontLayerPick.js. The calibration of the measures on
// synthetic surfaces is kept in surfaceEstimator.test.js.)

import {jacobiEigenSymmetric3} from "./volume/referencePlane.js";

const MIN_VARIANCE = 1e-12; // m^2 - below this the points are (numerically) coincident

// A fresh, fully-initialised result object. Callers keep ONE and pass it to
// every estimateSurface() call (no per-call allocation).
export function createSurfaceFit(){
	return {
		ok: false, reason: "few",
		nx: 0, ny: 0, nz: 1,
		cx: 0, cy: 0, cz: 0,
		n: 0,
		l0: 0, l1: 0, l2: 0,
		rms: 0, planarity: 0, linearity: 0, curvature: 0,
	};
}

// pts: Float32Array|Float64Array|number[], x,y,z interleaved, expressed
// RELATIVE to a nearby origin (so Float32 keeps full precision on UTM-scale
// clouds); n: number of points to use. Fills `out` and returns it.
// ok:false + reason ("few" | "coincident" | "collinear" | "nonfinite") means
// no normal can be derived - callers must not use nx/ny/nz then.
export function estimateSurface(pts, n, out = createSurfaceFit()){
	out.n = n;
	out.ok = false;
	if(!(n >= 3)){ out.reason = "few"; return out; }

	let sx = 0, sy = 0, sz = 0;
	for(let i = 0, e = 3 * n; i < e; i += 3){
		sx += pts[i]; sy += pts[i + 1]; sz += pts[i + 2];
	}
	const cx = sx / n, cy = sy / n, cz = sz / n;

	let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
	for(let i = 0, e = 3 * n; i < e; i += 3){
		const a = pts[i] - cx, b = pts[i + 1] - cy, c = pts[i + 2] - cz;
		xx += a * a; xy += a * b; xz += a * c;
		yy += b * b; yz += b * c; zz += c * c;
	}
	const M = [xx / n, xy / n, xz / n, yy / n, yz / n, zz / n];
	if(!(isFinite(M[0]) && isFinite(M[1]) && isFinite(M[2]) && isFinite(M[3]) && isFinite(M[4]) && isFinite(M[5]))){
		out.reason = "nonfinite";
		return out;
	}

	const {eigenvalues, eigenvectors} = jacobiEigenSymmetric3(M);
	let i0 = 0, i2 = 0;
	for(let i = 1; i < 3; i++){
		if(eigenvalues[i] < eigenvalues[i0]) i0 = i;
		if(eigenvalues[i] > eigenvalues[i2]) i2 = i;
	}
	if(i0 === i2){ i2 = (i0 + 1) % 3; } // all three equal (isotropic)
	const i1 = 3 - i0 - i2;

	const l0 = Math.max(eigenvalues[i0], 0);
	const l1 = Math.max(eigenvalues[i1], 0);
	const l2 = Math.max(eigenvalues[i2], 0);

	out.cx = cx; out.cy = cy; out.cz = cz;
	out.l0 = l0; out.l1 = l1; out.l2 = l2;
	out.rms = Math.sqrt(l0);
	out.planarity = l1 > MIN_VARIANCE ? 1 - l0 / l1 : 0;
	out.linearity = l2 > MIN_VARIANCE ? 1 - l1 / l2 : 0;
	out.curvature = (l0 + l1 + l2) > MIN_VARIANCE ? l0 / (l0 + l1 + l2) : 0;

	if(l2 <= MIN_VARIANCE){ out.reason = "coincident"; return out; }
	// collinear: the second-largest spread is negligible next to the largest,
	// so a plane through the points is undefined (any plane containing the line)
	if(l1 <= 1e-3 * l2){ out.reason = "collinear"; return out; }

	const v = eigenvectors[i0];
	const len = Math.hypot(v[0], v[1], v[2]);
	if(!(len > 1e-12)){ out.reason = "nonfinite"; return out; }
	out.nx = v[0] / len; out.ny = v[1] / len; out.nz = v[2] / len;
	out.ok = true;
	out.reason = null;
	return out;
}

// Deterministic sign: flip the normal so it points toward the viewer.
// (vx,vy,vz) = vector from the surface point to the camera. A normal exactly
// perpendicular to the view direction keeps its sign (no flicker source in
// practice: that is a grazing view where the disc is edge-on anyway).
export function orientTowards(fit, vx, vy, vz){
	if(fit.nx * vx + fit.ny * vy + fit.nz * vz < 0){
		fit.nx = -fit.nx; fit.ny = -fit.ny; fit.nz = -fit.nz;
	}
	return fit;
}
